import React, { useState } from "react";
import { Box, Button, CircularProgress, IconButton, InputAdornment, LinearProgress, Stack, TextField, Typography, Alert } from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { toast } from "react-toastify";
import api, { clearAuth } from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { extractErrorMessage } from "../../utils/errorMessage";

function evaluate(pwd) {
  const checks = [
    { test: (p) => p.length >= 10, label: "At least 10 characters" },
    { test: (p) => /[A-Z]/.test(p), label: "Uppercase letter" },
    { test: (p) => /[a-z]/.test(p), label: "Lowercase letter" },
    { test: (p) => /\d/.test(p), label: "Number" },
    { test: (p) => /[^A-Za-z0-9]/.test(p), label: "Symbol" },
  ];
  const passed = checks.filter((c) => c.test(pwd || ""));
  return { score: passed.length, total: checks.length, checks };
}

export default function ResetPasswordNew() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const authed = Boolean(sessionStorage.getItem("token"));
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const strength = evaluate(newPwd);

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd !== confirm) return toast.error("Passwords do not match");
    if (strength.score < 4) return toast.error("Password doesn't meet requirements");
    setLoading(true);
    try {
      if (token) {
        await api.post(endpoints.auth.resetPassword, { token, newPassword: newPwd });
        setDone(true);
      } else if (authed) {
        await api.post("/api/security/change-password", { currentPassword: currentPwd, newPassword: newPwd });
        toast.success("Password updated successfully. Please sign in again.");
        clearAuth();
        setRedirecting(true);
        setTimeout(() => {
          nav("/login", { replace: true });
        }, 1200);
      } else {
        throw new Error("No token or active session");
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 440 }}>
        {!authed && (
          <Button component={Link} to="/login" startIcon={<ArrowBackOutlinedIcon />} sx={{ mb: 3, color: "text.secondary" }}>
            Back to sign in
          </Button>
        )}

        <Box sx={{ width: 56, height: 56, borderRadius: 3, background: "linear-gradient(135deg, #EDE8FA 0%, #F4EDFA 100%)", color: "primary.main", display: "flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
          {done ? <CheckCircleOutlineIcon sx={{ fontSize: 28 }} /> : <LockResetOutlinedIcon sx={{ fontSize: 28 }} />}
        </Box>

        {done ? (
          <>
            <Typography sx={{ fontSize: "1.75rem", fontWeight: 800, mb: 1, color: "success.main" }}>
              Password updated
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 3 }}>
              {authed ? "Your password has been changed successfully." : "You can now sign in with your new password."}
            </Typography>
            <Button fullWidth variant="contained" size="large" onClick={() => nav("/login")} sx={{ py: 1.5 }}>
              Sign in
            </Button>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: "1.75rem", fontWeight: 800, mb: 1 }}>
              {authed ? "Change password" : "Set new password"}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 3 }}>
              {authed ? "Update the password for your account." : "Choose a strong password to regain access."}
            </Typography>
            <Box component="form" onSubmit={submit}>
              <Stack spacing={2}>
                {authed && (
                  <TextField
                    fullWidth type={show ? "text" : "password"} label="Current password"
                    value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)}
                    autoComplete="current-password"
                  />
                )}
                <TextField
                  fullWidth type={show ? "text" : "password"} label="New password"
                  value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShow((s) => !s)}>
                          {show ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {newPwd && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary" }}>PASSWORD STRENGTH</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: strength.score < 3 ? "error.main" : strength.score < 5 ? "warning.main" : "success.main" }}>
                        {strength.score < 3 ? "WEAK" : strength.score < 5 ? "MEDIUM" : "STRONG"}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(strength.score / strength.total) * 100}
                      sx={{
                        height: 6, borderRadius: 3, mb: 1.5,
                        "& .MuiLinearProgress-bar": {
                          background: strength.score < 3 ? "#ff1f35" : strength.score < 5 ? "#f9bc06" : "#39c645",
                        },
                      }}
                    />
                    <Stack spacing={0.5}>
                      {strength.checks.map((c) => {
                        const ok = c.test(newPwd);
                        return (
                          <Stack key={c.label} direction="row" spacing={1} alignItems="center">
                            <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: ok ? "success.main" : "divider", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {ok && <CheckCircleOutlineIcon sx={{ fontSize: 10, color: "white" }} />}
                            </Box>
                            <Typography sx={{ fontSize: 11, color: ok ? "success.main" : "text.secondary" }}>
                              {c.label}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
                <TextField
                  fullWidth type={show ? "text" : "password"} label="Confirm new password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  error={confirm && confirm !== newPwd}
                  helperText={confirm && confirm !== newPwd ? "Passwords don't match" : ""}
                />
                <Button type="submit" fullWidth variant="contained" size="large" disabled={loading || redirecting || strength.score < 4 || newPwd !== confirm} sx={{ py: 1.5 }}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : redirecting ? "Redirecting to sign in…" : "Update password"}
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
